import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiExpandableBox,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ORDER_STATUS_COLORS,
} from "@miplatform/constants";
import {
  addSpaces,
  getContrastColor,
  isEmpty,
  toFormattedDate,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { WorkOrderCount } from "../WorkOrderCount/WorkOrderCount";

const PLACEHOLDER_TEXT = "--/--/--";
const DATE_FORMAT = "MM/DD/YY";

const getColor = (status) => ORDER_STATUS_COLORS[status];

export function OrderInfoPopper({ order }) {
  const {
    orderTemplateName,
    orderTemplateTradeName,
    orderTemplateColor,
    home,
    workOrders,
  } = order;

  const { classes, theme } = useStyles({ orderTemplateColor });

  return (
    <Tooltip
      arrow
      title={
        <Box className={classes.popperContainer}>
          <Box className={classes.popperHeader}>
            <Typography>{orderTemplateName}</Typography>
          </Box>
          <Box className={classes.popperAddress}>
            <Typography
              fontWeight={theme.fontWeight.semiBold}
              fontSize={theme.fontSize.medium}
            >
              {home.address.addressLine1} | {home.builderName}
            </Typography>
            <Typography
              color={theme.palette.bluegrey.main}
              fontSize={theme.fontSize.medium}
            >
              {home.communityName} {home.communityName && " | "}
              {home.address.city}, {home.address.regionCode} |{" "}
              {home.address.postalCode}
            </Typography>
          </Box>
          <Box className={classes.popperDates}>
            {order.approvedStartDate ? (
              <MiStackedTypography
                title="Scheduled"
                description={
                  order.approvedStartDate
                    ? toFormattedDate(order.approvedStartDate, DATE_FORMAT)
                    : PLACEHOLDER_TEXT
                }
                descriptionProps={{
                  fontWeight: theme.fontWeight.semiBold,
                }}
              />
            ) : (
              <MiStackedTypography
                title="Requested"
                description={
                  order.requestedStartDate
                    ? toFormattedDate(order.requestedStartDate, DATE_FORMAT)
                    : PLACEHOLDER_TEXT
                }
                descriptionProps={{
                  fontWeight: theme.fontWeight.semiBold,
                }}
              />
            )}
            <MiStackedTypography
              title="Completed"
              description={
                order.completeDate
                  ? toFormattedDate(order.completeDate, DATE_FORMAT)
                  : PLACEHOLDER_TEXT
              }
              descriptionProps={{
                fontWeight: theme.fontWeight.semiBold,
                color: order.completeDate
                  ? getColor(order.orderStatus)
                  : "inherit",
              }}
            />
            <MiStackedTypography
              title="Trade Responsible"
              description={orderTemplateTradeName}
              descriptionProps={{
                fontWeight: theme.fontWeight.semiBold,
              }}
            />
          </Box>
          {!isEmpty(workOrders) && (
            <MiExpandableBox
              defaultExpanded
              title="Work Orders"
              rightHeaderContent={<WorkOrderCount order={order} />}
              sx={{ pt: 1, padding: theme.spacing(1, 2) }}
            >
              <Box className={classes.workOrdersContainer}>
                {workOrders.map((workOrder) => {
                  const workOrderStatusColor = getColor(workOrder.status);

                  return (
                    <Box key={workOrder.id} className={classes.workOrderItem}>
                      <Typography
                        fontSize={theme.fontSize.medium}
                        fontWeight={theme.fontWeight.semiBold}
                      >
                        {workOrder.templateName}
                      </Typography>
                      <Box
                        className={classes.workOrderStatusContainer}
                        style={{ backgroundColor: workOrderStatusColor }}
                      >
                        <Typography
                          fontSize={theme.fontSize.small}
                          fontWeight={theme.fontWeight.semiBold}
                          color={getContrastColor(workOrderStatusColor)}
                        >
                          {addSpaces(workOrder.status)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </MiExpandableBox>
          )}
        </Box>
      }
      slotProps={{
        popper: {
          placement: "right",
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, -18] },
            },
          ],
          sx: { p: 0, m: 0 },
        },
        tooltip: {
          sx: {
            p: 0,
            m: 0,
            "& .MuiTooltip-arrow": {
              color: theme.palette.background.paper,
            },
          },
        },
      }}
    >
      <Box>
        <MiButton
          size="small"
          className={classes.infoButton}
          variant={BUTTON_VARIANTS.CONTAINED}
          color={BUTTON_COLORS.PRIMARY}
          startIcon={{
            path: mdi.mdiInformationSlabCircleOutline,
            iconSize: 0.5,
          }}
        />
      </Box>
    </Tooltip>
  );
}

const useStyles = makeStyles()((theme, { orderTemplateColor } = {}) => ({
  infoButton: {
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0.3, 1.5),
    marginLeft: theme.spacing(1),
  },
  popperContainer: {
    width: 320,
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.common.black,
    boxShadow: theme.shadows[4],
    borderRadius: theme.spacing(1.5),
  },
  popperHeader: {
    display: "flex",
    alignItems: "center",
    boxSizing: "content-box",
    borderRadius: theme.spacing(1.5, 1.5, 0, 0),
    padding: theme.spacing(1.5, 2),
    backgroundColor: orderTemplateColor,
    color: getContrastColor(orderTemplateColor),
    height: 20,
  },
  popperAddress: {
    padding: theme.spacing(2),
  },
  popperDates: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey.mediumLight,
  },
  workOrdersContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    overflow: "auto",
    maxHeight: 250,
  },
  workOrderItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.grey.darkAccent}`,
    backgroundColor: theme.palette.grey.lightAccent,
  },
  workOrderStatusContainer: {
    padding: theme.spacing(0.25, 1.5),
    borderRadius: theme.shape.borderRadius,
  },
}));
